import cx from "classnames";
import {
  type MouseEventHandler,
  type ReactElement,
  cloneElement,
  useState,
} from "react";

import { SelectButton } from "metabase/common/components/SelectButton";
import { useCaptureEvent } from "metabase/common/hooks";
import { ParameterTargetList } from "metabase/parameters/components/ParameterTargetList";
import {
  type ParameterMappingOption,
  getMappingOptionByTarget,
} from "metabase/parameters/utils/mapping-options";
import { Popover } from "metabase/ui";
import type Question from "metabase-lib/v1/Question";
import type { ParameterTarget } from "metabase-types/api";

type TriggerRenderProps = {
  selected?: ParameterMappingOption;
  disabled: boolean;
  placeholder?: string;
};

type TriggerElement = ReactElement<{
  className?: string;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
}>;

type ParameterTargetWidgetProps = {
  question?: Question;
  target?: ParameterTarget | null;
  onChange: (target: ParameterTarget) => void;
  mappingOptions: ParameterMappingOption[];
  placeholder?: string;
  children?: TriggerElement | ((props: TriggerRenderProps) => TriggerElement);
};

const defaultChildren = ({
  selected,
  disabled,
  placeholder,
}: TriggerRenderProps) => {
  const label = selected ? selected.name : placeholder || "Select a target";
  return (
    <SelectButton
      hasValue={!!selected}
      disabled={disabled}
      className="border-med"
      aria-label={label}
    >
      {label}
    </SelectButton>
  );
};

export function ParameterTargetWidget({
  question,
  target,
  onChange,
  mappingOptions,
  placeholder,
  children = defaultChildren,
}: ParameterTargetWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);

  useCaptureEvent(
    "keydown",
    (e) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        setIsOpen(false);
      }
    },
    { enabled: isOpen },
  );

  const disabled = mappingOptions.length === 0;
  const selected = getMappingOptionByTarget(mappingOptions, target, question);

  const triggerElement =
    typeof children === "function"
      ? children({ selected, disabled, placeholder })
      : children;
  const targetElement = cloneElement(triggerElement, {
    className: cx(triggerElement.props.className, { disabled }),
    disabled,
    onClick: (event) => {
      triggerElement.props.onClick?.(event);
      if (!event.isPropagationStopped()) {
        setIsOpen((isOpen) => !isOpen);
      }
    },
  });

  return (
    <Popover
      withinPortal={false}
      position="bottom-start"
      closeOnClickOutside
      trapFocus
      disabled={disabled}
      opened={isOpen}
      onChange={setIsOpen}
    >
      <Popover.Target>{targetElement}</Popover.Target>
      <Popover.Dropdown style={{ boxSizing: "content-box" }}>
        <ParameterTargetList
          onChange={(target) => {
            onChange(target);
            setIsOpen(false);
          }}
          mappingOptions={mappingOptions}
          selectedMappingOption={selected}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
